import { css } from "@emotion/react";
import type React from "react";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { useCatalogState } from "../../hooks/useCatalogState";
import type { Product } from "../../hooks/useInventoryState";
import { useShoppingCartItemsState } from "../../hooks/useShoppingCartItemsState";
import { useSiteLayoutState } from "../../hooks/useSiteLayoutState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { FwNumberDropDown } from "../../lib/FwReact/FwNumberDropDown";
import type { ImmutableObject } from "../../lib/immutable";

export type AddToCartProps = {
	product: ImmutableObject<Product>;
	shoppingCartMode?: boolean;
	showQtyInCart?: boolean;
	minHeight?: string;
};
export const AddOrUpdateCart: React.FC<AddToCartProps> = (props) => {
	const product = props.product;
	const shoppingCartItemsState = useShoppingCartItemsState();
	const shoppingCartItem = shoppingCartItemsState
		.get()
		.find((i) => i.product.Id === props.product.Id);
	const shoppingCartQty = shoppingCartItemsState.getQty(product);
	const tooltipId = `AddToCart-${product.Id}`;
	const siteLayoutState = useSiteLayoutState();
	const isAvailable = product && (product.Availability?.TotalIn ?? 0) > 0;
	const translate = useTranslate();

	const initialQtyToDisplay = props.shoppingCartMode
		? shoppingCartQty
		: shoppingCartQty > 0
			? shoppingCartQty
			: 1;

	//console.log({
	//    product: product.ItemName,
	//    shoppingCartQty: shoppingCartQty,
	//    initialQtyToDisplay: initialQtyToDisplay
	//})

	// split button
	const [qty, setQty] = useState<number>(initialQtyToDisplay);

	const catalogState = useCatalogState();
	const catalog = catalogState.getProductCatalog(product);
	useEffect(() => {
		if (catalog) {
			setQty(initialQtyToDisplay);
		}
	}, [catalog, initialQtyToDisplay]);
	if (!catalog) {
		return null;
	}

	const canAddtoCart =
		isAvailable ||
		catalog.AllowAddingUnavailableItems ||
		props.shoppingCartMode;

	const qtyIn = product.Availability?.TotalIn ?? 0;
	const qtyAvailable = qtyIn - shoppingCartQty;
	const minHeight = props.minHeight ?? "auto";
	let maxQty: number | undefined;
	if (props.shoppingCartMode) {
		if (!catalog.AllowAddingUnavailableItems) {
			maxQty = qtyIn;
		}
	} else {
		if (!catalog.AllowAddingUnavailableItems) {
			maxQty = qtyIn;
		}
	}

	// Split button
	const ADD_TO_CART = translate("Add to cart");
	const UPDATE_CART = translate("Update cart");
	const handleClick = () => {
		if (shoppingCartQty === 0) {
			addToCart();
		} else {
			updateCart();
		}
	};

	const addToCart = async () => {
		return Promise.resolve().then(() => {
			if (qty > 0) {
				const shoppingCartQty = shoppingCartItemsState.getQty(product);
				const newQty = shoppingCartQty + qty;
				if (!catalog.AllowAddingUnavailableItems && newQty > qtyAvailable) {
					siteLayoutState.showTooltip({
						id: tooltipId,
						variant: "error",
						place: "top",
						message:
							translate(
								"Cannot request more than then the total available qty.",
							) +
							": " +
							qtyAvailable.toString(),
					});
					return;
				}
				shoppingCartItemsState.setQty(product, newQty);
				setQty(newQty);
				siteLayoutState.showTooltip({
					id: tooltipId,
					variant: "success",
					place: "top",
					message: `${translate("Added")}: ${qty.toString()}`,
				});
			}
		});
	};

	const updateCart = async () => {
		return Promise.resolve().then(() => {
			const updatedQty = qty;
			//const shoppingCartQty = shoppingCartItemsState.getQty(product);
			if (!catalog.AllowAddingUnavailableItems && updatedQty > qtyIn) {
				siteLayoutState.showTooltip({
					id: tooltipId,
					variant: "error",
					place: "top",
					message:
						translate(
							"Cannot request more than then the total available qty.",
						) +
						": " +
						qtyAvailable.toString(),
				});
				return;
			}
			shoppingCartItemsState.setQty(product, updatedQty);
			siteLayoutState.showTooltip({
				id: tooltipId,
				variant: "success",
				place: "top",
				message: `${translate("Updated")}: ${updatedQty.toString()}`,
			});
		});
	};

	return (
		<div
			css={css`
				flex: 0 0 auto;
				padding:0;
				min-height:${minHeight};
				display:flex;
				flex-direction:column;
				justify-content:center;
				align-items:center;
				font-size: .7rem;
				`}
		>
			{props.showQtyInCart && shoppingCartQty > 0 && (
				<div
					css={css`display:flex;justify-content:center;align-items:center;padding:.5rem;font-size:.8rem;background-color:#f44336;color:#ffffff;border-radius: .5em;`}
				>
					{`${shoppingCartItemsState.getQty(product)} ${translate("in cart")}`}
				</div>
			)}
			{canAddtoCart && (
				<div
					css={css`margin: .5rem auto;border-radius: 4px;background-color: ${shoppingCartItem?.hasQtyError ? "pink" : "transparent"}`}
				>
					<FwNumberDropDown
						label={translate("Qty")}
						labelPrefix={translate("Qty")}
						value={props.shoppingCartMode ? shoppingCartQty : qty}
						maxQty={maxQty}
						onChange={(value) => {
							if (props.shoppingCartMode) {
								shoppingCartItemsState.setQty(product, value);
							} else {
								setQty(value);
							}
						}}
					/>
				</div>
			)}
			{canAddtoCart && !props.shoppingCartMode && (
				<div
					css={css`display:flex;justify-content:center;align-items:center;width:10rem;`}
				>
					<Button
						variant="outline"
						size="sm"
						className="border-box w-26 text-xs h-8"
						data-tooltip-id={tooltipId}
						onClick={handleClick}
					>
						{shoppingCartQty > 0 ? UPDATE_CART : ADD_TO_CART}
					</Button>
				</div>
			)}
		</div>
	);
};
